import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Award } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import DomesticEICStep1 from "@/components/certificates/DomesticEICStep1";
import DomesticEICStep2 from "@/components/certificates/DomesticEICStep2";
import DomesticEICStep3 from "@/components/certificates/DomesticEICStep3";
import DomesticEICStep4 from "@/components/certificates/DomesticEICStep4";
import DomesticEICStep5 from "@/components/certificates/DomesticEICStep5";
import RecentAddressPicker from "@/components/certificates/RecentAddressPicker";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: "Client & Installation" },
  { num: 2, label: "Supply & Earthing" },
  { num: 3, label: "Inspections" },
  { num: 4, label: "Circuits & Testing" },
  { num: 5, label: "Review & Generate" },
];

const INITIAL_FORM = {
  // Page 1 - Client & Installation
  client_name: "", client_address: "", client_email: "",
  installation_description: "", installation_extent: "",
  installation_address: "", installation_postcode: "",
  installation_type: "", // new, addition, alteration
  departures: "",
  designer_name: "", designer_signature: "",
  constructor_name: "", constructor_signature: "",
  cert_date: null,
  contractor_trading_title: "", contractor_email: "", contractor_phone: "",
  contractor_web: "", contractor_reg_no: "", contractor_branch_no: "",
  contractor_address: "", contractor_postcode: "",
  next_inspection_interval: "",
  comments: "",
  // Page 2 - Supply Characteristics
  supply_system: "", // TN-S, TN-C-S, TT, TN-C-S (PNB)
  supply_conductors: "", // 1-phase 2 wire, 1-phase 3 wire, etc
  supply_type: "AC",
  nominal_voltage: "230",
  nominal_frequency: "50",
  external_ze: "",
  pfc: "",
  primary_supply_bs: "", primary_supply_type: "", primary_supply_rated_current: "",
  primary_supply_short_circuit: "",
  earthing_means: "", // distributor, installation_electrode, other
  earth_electrode_type: "", earth_electrode_resistance: "",
  measured_ze: "", ze_method: "", electrode_location: "",
  earthing_conductor_material: "Copper", earthing_conductor_csa: "",
  bonding_gas: false, bonding_water: false, bonding_oil: false,
  bonding_structural: false, bonding_other: "",
  supply_conductor_material: "Copper", supply_conductor_csa: "",
  main_switch_type: "", main_switch_voltage: "230", main_switch_rated_current: "",
  main_rcd_current: "", main_rcd_time: "", main_rcd_delay: "",
  max_demand: "", num_smoke_alarms: "", num_poles: "2",
  // Page 3 - Schedule of Inspections
  inspections: {
    "1.0": "N/A", "2.0": "N/A", "3.0": "N/A", "4.0": "N/A",
    "5.0": "N/A", "6.0": "N/A", "7.0": "N/A", "8.0": "N/A",
    "9.0": "N/A", "10.0": "N/A", "11.0": "N/A", "12.0": "N/A",
    "13.0": "N/A", "14.0": "N/A",
  },
  // Page 4 - Test Instruments & Circuits
  instrument_earth_loop: "", instrument_insulation: "",
  instrument_continuity: "", instrument_rcd: "",
  instrument_mft: "", instrument_other: "",
  vulnerable_circuits: "",
  db_ref: "DB1", db_location: "", db_ze: "", db_ipf: "",
  db_main_switch_type: "", db_supplied_from: "", db_rating: "",
  db_num_phases: "1", db_spd: "",
  db_supply_device_ref: "", db_earth_csa: "", db_neutral_csa: "",
  db_supply_device_rating: "",
  circuits: [
    { ref: "1", designation: "", csa_live: "", csa_cpc: "", wiring_type: "A", points: "",
      ocpd_type: "", ocpd_rating: "", ocpd_breaking: "", ocpd_max_disconnect: "",
      rcd_type: "", rcd_idn: "", rcd_rating: "",
      r1r2: "", r2: "", ring_r1: "", ring_rn: "", ring_r2: "",
      ir_voltage: "500", ir_live_live: "", ir_live_earth: "",
      zs: "", max_zs: "", polarity: "", rcd_time: "", rcd_test_btn: "", afdd: "" },
  ],
};

export default function DomesticEICWizard() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [editCertId, setEditCertId] = useState(null);
  const [formData, setFormData] = useState({
    ...INITIAL_FORM,
    contractor_trading_title: user?.company || "",
    contractor_email: user?.email || "",
    designer_name: user?.name || "",
    constructor_name: user?.name || "",
  });

  useEffect(() => {
    // Check for edit data
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        setFormData((prev) => ({ ...prev, ...cert, cert_date: null }));
        toast.info("Editing certificate — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }
    // Check for duplicate
    const dupData = sessionStorage.getItem("duplicate_cert_data");
    if (dupData) {
      try {
        const cert = JSON.parse(dupData);
        sessionStorage.removeItem("duplicate_cert_data");
        setFormData((prev) => ({ ...prev, ...cert, cert_date: null }));
        return;
      } catch { /* ignore */ }
    }
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved && saved.contractor_trading_title) {
          setFormData((prev) => ({
            ...prev,
            ...Object.fromEntries(Object.entries(saved).filter(([, v]) => v)),
          }));
        }
      }).catch(() => {});
  }, []);

  const updateForm = (updates) => setFormData((prev) => ({ ...prev, ...updates }));
  const nextStep = () => setStep((s) => Math.min(s + 1, 5));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="domestic-eic-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Title */}
        <div className="flex items-center gap-3 mb-4">
          <Award className="w-6 h-6 text-[#00A3E0]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Domestic Electrical Installation Certificate
          </h1>
        </div>

        <RecentAddressPicker onSelect={(a) => updateForm({
          installation_address: a.address, installation_postcode: a.postcode,
          client_name: a.client_name || formData.client_name,
          client_email: a.client_email || formData.client_email,
        })} />

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8 mt-6" data-testid="eic-step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  data-testid={`eic-step-${s.num}-indicator`}
                  className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${
                    step > s.num ? "step-completed" : step === s.num ? "step-active" : "step-inactive"
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 font-medium text-center hidden sm:block ${step >= s.num ? "text-[#0A0A0A]" : "text-[#B8B9BD]"}`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#00A3E0]" : "bg-[#E4E4E7]"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="animate-fade-in-up" key={step}>
          {step === 1 && <DomesticEICStep1 formData={formData} updateForm={updateForm} onNext={nextStep} />}
          {step === 2 && <DomesticEICStep2 formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} />}
          {step === 3 && <DomesticEICStep3 formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} />}
          {step === 4 && <DomesticEICStep4 formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} />}
          {step === 5 && <DomesticEICStep5 formData={formData} onPrev={prevStep} user={user} editCertId={editCertId} />}
        </div>
      </div>
    </div>
  );
}
